import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useCustomers } from '../../../queries/customerQueries';

/**
 * Searchable Multi-Select Dropdown Component for Importer View Permission
 */
const ImporterMultiSelect = ({ value = [], onChange, disabled, options = [], error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!search?.trim()) return options || [];
    return (options || []).filter((opt) =>
      opt?.label?.toLowerCase()?.includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleToggle = (optVal) => {
    if (disabled) return;
    const current = Array.isArray(value) ? value : [];
    if (current.includes(optVal)) {
      onChange(current.filter((v) => v !== optVal));
    } else {
      onChange([...current, optVal]);
    }
  };

  const handleRemove = (e, optVal) => {
    e?.stopPropagation();
    if (disabled) return;
    const current = Array.isArray(value) ? value : [];
    onChange(current.filter((v) => v !== optVal));
  };

  const selectedLabels = useMemo(() => {
    const current = Array.isArray(value) ? value : [];
    return (options || []).filter((opt) => current.includes(opt?.id) || current.includes(opt?.label));
  }, [value, options]);

  return (
    <div className="position-relative w-100" ref={containerRef}>
      {/* Dropdown trigger bar */}
      <div
        className={`form-control d-flex flex-wrap align-items-center gap-1 min-h-38 ${disabled ? 'bg-light text-muted' : ''} ${error ? 'is-invalid' : ''}`}
        style={{ minHeight: '38px', cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {disabled ? (
          <span className="text-muted small fst-italic">All Importers (Select All Enabled)</span>
        ) : (selectedLabels?.length || 0) > 0 ? (
          selectedLabels?.map((opt) => (
            <span key={opt?.id || opt?.label} className="badge bg-primary text-white d-inline-flex align-items-center gap-1 px-2 py-1 small">
              {opt?.label}
              <span
                role="button"
                className="ms-1 fw-bold"
                onClick={(e) => handleRemove(e, opt?.id || opt?.label)}
                style={{ fontSize: '11px', lineHeight: 1, cursor: 'pointer' }}
              >
                ✕
              </span>
            </span>
          ))
        ) : (
          <span className="text-muted small">Search &amp; Select Importers...</span>
        )}
        <span className="ms-auto text-muted small" style={{ fontSize: '10px' }}>▼</span>
      </div>

      {/* Floating Dropdown */}
      {isOpen && !disabled && (
        <div
          className="position-absolute start-0 end-0 bg-white border rounded shadow-sm p-2 mt-1"
          style={{ zIndex: 1050, maxHeight: '240px', overflowY: 'auto' }}
        >
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder="Search importer by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {(filteredOptions?.length || 0) === 0 ? (
            <div className="text-muted small py-2 text-center">No importers found</div>
          ) : (
            filteredOptions?.map((opt) => {
              const currentVal = Array.isArray(value) ? value : [];
              const isChecked = currentVal.includes(opt?.id) || currentVal.includes(opt?.label);
              return (
                <div
                  key={opt?.id || opt?.label}
                  className="py-1.5 px-2 rounded d-flex align-items-center gap-2 border-bottom border-light"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleToggle(opt?.id || opt?.label)}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-0 flex-shrink-0"
                    checked={isChecked}
                    onChange={() => { }}
                    style={{ cursor: 'pointer', float: 'none', marginLeft: 0 }}
                  />
                  <span className="small text-dark flex-grow-1 text-truncate" style={{ cursor: 'pointer' }}>
                    {opt?.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {error?.message && <div className="invalid-feedback d-block mt-1">{error.message}</div>}
    </div>
  );
};

/**
 * BlDetailsTab — BL DETAILS tab
 * standard Bootstrap 5 horizontal form layout (col-5 label, col-7 input)
 * Uniform grid split ensures all input fields align perfectly at the same horizontal level.
 */
const BlDetailsTab = ({ control, errors, setValue }) => {
  const selectAllImporters = useWatch({ control, name: 'viewPermission.selectAll' });
  const { data: apiCustomers } = useCustomers();

  const customerOptions = useMemo(() => {
    const raw = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);
    return (raw || [])
      ?.map((c) => ({
        id: c?.id || c?.customerId || c?.customerName || c?.name,
        label: c?.customerName || c?.companyName || c?.name || `Customer #${c?.id}`,
      }))
      ?.filter((opt) => opt?.label);
  }, [apiCustomers]);

  return (
    <div className="w-100">
      <h5 className="de-section-title">BL DETAILS</h5>

      <div className="row g-3">
        {/* FILE NAME */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">FILE NAME</label>
            <div className="col-7">
              <Controller
                name="fileName"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="File Name" />
                )}
              />
            </div>
          </div>
        </div>

        {/* BL NO */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">BL NO :</label>
            <div className="col-7">
              <Controller
                name="billno"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="BL Number" />
                )}
              />
            </div>
          </div>
        </div>

        {/* DATE OF SHIPPING */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">DATE OF SHIPPING :</label>
            <div className="col-7">
              <Controller
                name="dateOfShipping"
                control={control}
                render={({ field }) => (
                  <input
                    type="date"
                    className="form-control"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* ETA DATE */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">ETA DATE :</label>
            <div className="col-7">
              <Controller
                name="etaDate"
                control={control}
                render={({ field }) => (
                  <input
                    type="date"
                    className="form-control"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* PQ WEIGHT */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">PQ WEIGHT:</label>
            <div className="col-7">
              <Controller
                name="pqWeightcheck"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className="form-control"
                    placeholder="pqWeightcheck"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* PQ WEIGHT 4* */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">PQ WEIGHT 4*:</label>
            <div className="col-7">
              <Controller
                name="pqWeight"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    className="form-control"
                    placeholder="PQ Weight"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* PQ RELAX (1500) */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">PQ RELAX (1500) :</label>
            <div className="col-7">
              <Controller
                name="pqFumigation"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    className="form-control"
                    placeholder="PQ Relax"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* CONTAINER NOS */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">CONTAINER NOS :</label>
            <div className="col-7">
              <Controller
                name="containersNumber"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className="form-control"
                    placeholder="Container Nos"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* COMMODIFY */}
        <div className="col-12 col-md-6 col-lg-4">
          <div className="row align-items-center g-2">
            <label className="col-5 col-form-label de-label text-start">COMMODIFY :</label>
            <div className="col-7">
              <Controller
                name="commodify"
                control={control}
                render={({ field }) => (
                  <input {...field} type="text" className="form-control" placeholder="Commodify" />
                )}
              />
            </div>
          </div>
        </div>

        {/* ── IMPORTER VIEW PERMISSION ── */}
        <div className="col-12 mt-4 pt-3 border-top">
          <div className="fw-semibold text-uppercase text-secondary mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
            IMPORTER VIEW PERMISSION
          </div>
          <div className="row align-items-center g-2">
            <label className="col-12 col-md-2 col-form-label de-label text-start">IMPORTER :</label>
            <div className="col-12 col-md-7">
              <Controller
                name="viewPermission.importerIds"
                control={control}
                render={({ field }) => (
                  <ImporterMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!!selectAllImporters}
                    options={customerOptions}
                    error={errors?.viewPermission?.importerIds || errors?.allowedImporters}
                  />
                )}
              />
            </div>
            <div className="col-12 col-md-3">
              <Controller
                name="viewPermission.selectAll"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="d-flex align-items-center gap-2 mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input mt-0 flex-shrink-0"
                      id="selectAllImportersCheck"
                      checked={!!value}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChange(checked);
                        if (checked && setValue) {
                          setValue('viewPermission.importerIds', []);
                        }
                      }}
                      style={{ cursor: 'pointer', width: '16px', height: '16px', float: 'none', marginLeft: 0 }}
                    />
                    <label className="form-check-label small fw-semibold text-dark mb-0 ms-1" htmlFor="selectAllImportersCheck" style={{ cursor: 'pointer' }}>
                      Select All
                    </label>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlDetailsTab;
